import React, { useEffect, useRef, useState } from "react";
import NavBar from "../components/NavBar/NavBar";
import Footer from "../components/Footer/Footer";
import HeroImg from "../components/HeroImg/HeroImg";
import Work from "../components/Work/Work";
import AboutContent from "../components/AboutContent/AboutContent";
import Experience from "../components/Experience/Experience";
import Contact from "../components/Form/Form";
import "./HomeStyles.css";

const Home = () => {
  const [visibleSections, setVisibleSections] = useState(new Set(['hero']));
  const [scrollProgress, setScrollProgress] = useState(0);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [isBreathing, setIsBreathing] = useState(false);
  const scrollTimeoutRef = useRef(null);
  const containerRef = useRef(null);
  const sectionsRef = useRef({
    hero: useRef(null),
    projects: useRef(null),
    about: useRef(null),
    experience: useRef(null),
    contact: useRef(null)
  });

  const scrollToSection = (sectionName) => {
    const section = sectionsRef.current[sectionName]?.current;
    if (section) {
      section.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  useEffect(() => {
    // Smooth scroll behavior para toda la página
    document.documentElement.style.scrollBehavior = 'smooth';
    
    // Scroll progress y gradientes graduales
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);

      // Control gradual de gradientes usando CSS custom properties
      const scrollY = window.scrollY;
      const maxScroll = totalHeight;
      
      // Calcular intensidad del gradiente (0 a 1) - más gradual
      const gradientIntensity = Math.min(scrollY / (maxScroll * 0.6), 1);
      
      // Calcular opacidad del granulado (más visible y dinámico)
      const grainOpacity = Math.min(scrollY / (maxScroll * 0.4), 0.12);
      
      // Calcular opacidad de líneas de profundidad
      const lineOpacity = Math.min(scrollY / (maxScroll * 0.7), 0.4);
      
      // Calcular progreso del scroll para efectos adicionales
      const scrollProgress = Math.min(scrollY / maxScroll, 1);
      
      // Aplicar valores a CSS custom properties
      if (containerRef.current) {
        containerRef.current.style.setProperty('--gradient-intensity', gradientIntensity);
        containerRef.current.style.setProperty('--grain-opacity', grainOpacity);
        containerRef.current.style.setProperty('--line-opacity', lineOpacity);
        containerRef.current.style.setProperty('--scroll-progress', scrollProgress);
      }

      // Efecto de respiración al detener el scroll
      setIsBreathing(false);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        setIsBreathing(true);
      }, 500);
    };

    // Cursor personalizado
    const handleMouseMove = (e) => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    };

    // Intersection Observer para detectar secciones visibles
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '-50px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionName = entry.target.dataset.section;
          setVisibleSections(prev => new Set([...prev, sectionName]));
        }
      });
    }, observerOptions);

    // Observar todas las secciones
    Object.entries(sectionsRef.current).forEach(([name, ref]) => {
      if (ref.current) {
        ref.current.dataset.section = name;
        observer.observe(ref.current);
      }
    });

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div ref={containerRef} className={`home-container ${isBreathing ? 'breathing' : ''}`}>
      {/* Efectos estéticos de fondo */}
      <div className="footer-filter"></div>
      <div className="depth-lines"></div>
      <div className="dynamic-gradient"></div>
      <div 
        className="cursor-repel"
        style={{
          '--cursor-x': `${(cursorPosition.x / window.innerWidth) * 100}%`,
          '--cursor-y': `${(cursorPosition.y / window.innerHeight) * 100}%`
        }}
      ></div>

      {/* Efecto de granulado extra */}
      <div className="extra-grain"></div>

      {/* Efecto de granulado muy intenso */}
      <div className="intense-grain"></div>

      {/* Fragmentos de código ocultos */}
      <div className="code-fragment">Let's build something amazing</div>
      <div className="code-fragment">Contact me for a unique site</div>
      <div className="code-fragment">Let's make your vision real</div>
      <div className="code-fragment">Creating digital experiences</div>
      <div className="code-fragment">Your project, my passion</div>
      <div className="code-fragment">Building the future web</div>
      <div className="code-fragment">Innovation meets design</div>
      <div className="code-fragment">Let's create together</div>
      
      {/* Indicador de Progreso */}
      <div className="scroll-progress">
        <div 
          className="scroll-progress-bar" 
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>

      {/* Partículas Sutiles */}
      <div className="particles">
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
      </div>


      <NavBar onNavigate={scrollToSection} />
      
      {/* Hero Section */}
      <section 
        ref={sectionsRef.current.hero} 
        className={`section hero-section ${visibleSections.has('hero') ? 'visible' : ''}`}
      >
        <HeroImg />
      </section>

      {/* Transición entre video y proyectos */}
      <div className="video-transition"></div>

      {/* Projects Section */}
      <section 
        id="projects"
        ref={sectionsRef.current.projects} 
        className="section projects-section"
      >
        <div className={`section-content ${visibleSections.has('projects') ? 'visible' : ''}`}>
          <Work />
        </div>
      </section>

      {/* About Section */}
      <section 
        id="about"
        ref={sectionsRef.current.about} 
        className="section about-section"
      >
        <div className={`section-content ${visibleSections.has('about') ? 'visible' : ''}`}>
          <AboutContent />
        </div>
      </section>

      {/* Experience Section */}
      <section 
        id="experience"
        ref={sectionsRef.current.experience} 
        className="section experience-section"
      >
        <div className={`section-content ${visibleSections.has('experience') ? 'visible' : ''}`}>
          <Experience />
        </div>
      </section>

      {/* Contact Section */}
      <section 
        id="contact"
        ref={sectionsRef.current.contact} 
        className="section contact-section"
      >
        <div className={`section-content ${visibleSections.has('contact') ? 'visible' : ''}`}>
          <Contact />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
